import { runAppLockboxEvaluation } from "../domain/evaluate.js";

/** @typedef {import('../domain/types.js').LockboxAppEntity} LockboxAppEntity */
/** @typedef {import('../domain/types.js').LockboxRulepack} LockboxRulepack */
/** @typedef {import('../domain/types.js').EnforcementPlan} EnforcementPlan */

/**
 * @typedef {'auto_blocked'|'user_blocked'|'mode_blocked'|'always_allowed'|'allowed'|'not_in_lockbox'} AppLibraryBadge
 */

/**
 * @typedef {Object} AppLibraryStatus
 * @property {AppLibraryBadge} badge
 * @property {string} label
 * @property {'#dc2626'|'#ea580c'|'#ca8a04'|'#16a34a'|'#6b7280'} color
 */

const MODE_LABELS = {
  normal: "Normal",
  work: "Work",
  sleep: "Sleep",
  strict: "Strict",
};

/**
 * @param {LockboxAppEntity} app
 * @param {EnforcementPlan|null} plan
 * @param {LockboxRulepack} rulepack
 * @param {string} activeModeId
 * @returns {AppLibraryStatus}
 */
export function getAppLibraryStatus(app, plan, rulepack, activeModeId) {
  const c = app.classification;

  if (c.userFlaggedSeverity || c.override?.kind === "force_block") {
    return {
      badge: "user_blocked",
      label: "User-blocked",
      color: "#ea580c",
    };
  }

  if (c.contentSeverity === "flagged" && c.severityAutoBlocked) {
    return {
      badge: "auto_blocked",
      label: "Auto-blocked (disturbing content)",
      color: "#dc2626",
    };
  }

  if (rulepack.alwaysAllowCategories.includes(c.primaryCategory) && plan?.allowAppIds?.includes(app.id)) {
    return {
      badge: "always_allowed",
      label: "Always allowed",
      color: "#16a34a",
    };
  }

  if (plan) {
    const world = {
      now: new Date(),
      activeModeId,
      emergency: { active: false, until: null },
      apps: [app],
    };
    const evalResult = runAppLockboxEvaluation(app, rulepack, world);
    const blocked = plan.blockAppIds.includes(app.id) || evalResult.decision === "block";

    if (blocked) {
      const modeLabel = MODE_LABELS[activeModeId] || activeModeId;
      return {
        badge: "mode_blocked",
        label: `Blocked in ${modeLabel}`,
        color: "#ca8a04",
      };
    }
    if (plan.allowAppIds.includes(app.id) || evalResult.decision === "allow") {
      return {
        badge: "allowed",
        label: "Allowed",
        color: "#16a34a",
      };
    }
  }

  return {
    badge: "not_in_lockbox",
    label: "Not restricted",
    color: "#6b7280",
  };
}

/**
 * @param {LockboxAppEntity[]} apps
 * @returns {Record<string, LockboxAppEntity[]>}
 */
export function groupAppsByCategory(apps) {
  /** @type {Record<string, LockboxAppEntity[]>} */
  const groups = {};
  for (const app of apps) {
    const cat = app.classification?.primaryCategory || "unknown";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(app);
  }
  const keys = Object.keys(groups).sort();
  return Object.fromEntries(keys.map((k) => [k, groups[k].sort((a, b) => a.displayName.localeCompare(b.displayName))]));
}
